import {
  CopyIcon,
  DownloadIcon,
  InputIcon,
  Pencil2Icon,
  ResetIcon,
  RowsIcon,
  TrashIcon,
  UploadIcon,
} from '@radix-ui/react-icons';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import {
  BaseballIcon,
  HMenuHideIcon,
  HMenuShowIcon,
  SuperAdminIcon,
} from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSimpleFileUploader } from 'components/common/file-uploader';
import { SectionHeader } from 'components/sections/header';
import { PitchListAvatarHeader } from 'components/sections/pitch-list/list/avatar-header';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { MachineContext } from 'contexts/machine.context';
import { PitchListDialog } from 'enums/pitch-lists';
import { t } from 'i18next';
import { TrainPitchesFn } from 'interfaces/function-types';
import { IMenuAction } from 'interfaces/i-menus';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { ERROR_MSGS } from 'lib_ts/enums/errors.enums';
import { TextMime } from 'lib_ts/enums/files.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavLocationState } from 'routes/nav-state';
import { paths } from 'routes/paths';
import { MainService } from 'services/main.service';
import { PitchListsService } from 'services/pitch-lists.service';
import slugify from 'slugify';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';
import useMachineStatusStore from 'stores/use-machine-status-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';
import { useVideosStore } from 'stores/use-videos-store';

export const PitchListHeader = (props: { onTrainFn: TrainPitchesFn }) => {
  const navigate = useNavigate();
  const isInGame = useAuthStore((s) => s.isInGame);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const s2mReady = useMachineStatusStore((s) => s.s2mReady);
  const readyToTrain = useTrainedMsStore((s) => s.readyToTrain);

  const showRecap = usePitchListStore((s) => s.showRecap);

  const activeList = useListLibraryStore((s) => s.activeList);
  const activeReadOnly = useListLibraryStore((s) => s.isActiveListReadOnly());

  const { activeModel, machine } = useContext(MachineContext);

  const pitches = usePitchListStore((s) => s.pitches);
  const loading = usePitchListStore((s) => s.loading);

  const [trainedPitches, untrainedPitches] = useMemo(() => {
    const dict: DictOf<IPitch[]> = ArrayHelper.groupByFn(
      pitches.filter((p) => !p.debugMs), // ignore debug pitches since they can't be trained
      (p) => (p._mx_tms?._mx_trained ? 'trained' : 'untrained')
    );

    // * coalesce to empty array for case when nothing is found
    return [dict['trained'] ?? [], dict['untrained'] ?? []];
  }, [pitches, machine.last_hardware_changed]);

  const fileInput = useRef<CommonSimpleFileUploader>(null);

  const listName = useMemo(() => {
    if (!activeList) {
      return t('common.loading').toString();
    }

    return activeList.name;
  }, [activeList]);

  const actions = useMemo<IMenuAction[]>(() => {
    const actions: IMenuAction[] = [
      {
        group: '_1',
        label: 'common.edit-list',
        prefixIcon: <Pencil2Icon />,
        invisible: activeReadOnly || isInGame,
        onClick: () => {
          useListLibraryStore.setState({
            dialogManage: {
              key: Date.now(),
              dialog: {
                identifier: 'PitchListListEditListDialog',
                mode: 'edit',
                onCreated: () =>
                  useListLibraryStore.setState({
                    dialogManage: undefined,
                  }),
                onClose: () =>
                  useListLibraryStore.setState({
                    dialogManage: undefined,
                  }),
              },
            },
          });
        },
      },
      {
        group: '_1',
        label: 'common.duplicate-list',
        prefixIcon: <CopyIcon />,
        invisible: isInGame,
        onClick: () => {
          useListLibraryStore.setState({
            dialogManage: {
              key: Date.now(),
              dialog: {
                identifier: 'PitchListListCopyListDialog',
                mode: 'copy',
                onCreated: () =>
                  useListLibraryStore.setState({
                    dialogManage: undefined,
                  }),
                onClose: () =>
                  useListLibraryStore.setState({
                    dialogManage: undefined,
                  }),
              },
            },
          });
        },
      },
      {
        group: '_1',
        label: 'common.reorder-list',
        prefixIcon: <RowsIcon />,
        invisible: activeReadOnly || isInGame,
        onClick: () =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.ReorderList,
            pitches,
          }),
      },
      {
        group: '_1',
        invisible: !machine.enable_trajekt_vision || isInGame,
        label: showRecap
          ? 'common.hide-vision-data'
          : 'common.show-vision-data',
        prefixIcon: showRecap ? <HMenuHideIcon /> : <HMenuShowIcon />,
        onClick: () => {
          if (machine.tracking_device !== TrackingDevice.TrajektVision) {
            // show a toast
            NotifyHelper.warning({
              message_md: t('common.toggle-x-tracking-device-msg', {
                x: TrackingDevice.TrajektVision,
              }),
            });
            return;
          }

          usePitchListStore.setState((prev) => ({
            showRecap: !prev.showRecap,
          }));
        },
      },
      {
        group: '_1',
        label: 'admin.restore-list',
        prefixIcon: <ResetIcon />,
        suffixIcon: <SuperAdminIcon />,
        invisible:
          activeReadOnly ||
          // only super admins and/or imposters can see this
          !userRoleFeatureFlags.isSuperAdmin,
        onClick: async () => {
          if (!activeList) {
            return;
          }

          NotifyHelper.success({
            message_md:
              'Pitch list restore started. Please wait, as this may take some time.',
          });

          const bs_hashes = await PitchListsService.getInstance().restore(
            activeList._id
          );

          if (bs_hashes.length > 0) {
            useTrainedMsStore
              .getState()
              .buildTmsDict(
                machine.machineID,
                machine.ball_type,
                machine.last_hardware_changed,
                true
              );
          }
        },
      },

      // divider

      {
        group: '_2',
        prefixIcon: <BaseballIcon />,
        label: 'common.create-pitch',
        disabled: isInGame,
        onClick: () => {
          setTimeout(() => {
            if (!activeList) {
              navigate(paths.pitches.design());
              return;
            }

            navigate(paths.pitches.design(activeList._id), {
              // keep track of the list ID so we can attach it as the parent of the new pitch
              state: {
                fromListId: activeList._id,
              } as NavLocationState,
            });
          }, 100);
        },
      },
      {
        group: '_2',
        prefixIcon: <UploadIcon />,
        label: 'common.upload-pitches',
        disabled: isInGame,
        onClick: () => {
          if (!activeList) {
            navigate(paths.pitches.upload);
            return;
          }

          navigate(paths.pitches.upload, {
            // keep track of the list ID so we can attach it as the parent of the new pitches
            state: { fromListId: activeList._id } as NavLocationState,
          });
        },
      },

      // divider

      {
        group: 'pl.bulk-edit',
        label: 'common.export-csv',
        prefixIcon: <DownloadIcon />,
        invisible: activeReadOnly || isInGame || pitches.length === 0,
        onClick: () => {
          if (!activeList) {
            return;
          }

          const videosDict = ArrayHelper.firstBy(
            useVideosStore.getState().videos,
            '_id'
          );

          const exPitches = pitches.map((p) =>
            PitchListHelper.convertToCustomExport({
              pitch: p,
              videosDict: videosDict,
              plate_ft: machine.releaseFt,
            })
          );

          // SessionEventsService.createOne({
          //   category: 'pitch',
          //   tags: 'export',
          //   data: {
          //     event: 'list (all) to CSV',
          //     list_id: activeList._id,
          //     list_name: activeList.name,
          //     count: exPitches.length,
          //   },
          // });

          MainService.getInstance()
            .convertJSONToCSV(exPitches)
            .then((csvString) => {
              const blob = new Blob([csvString], { type: 'text/csv' });
              MiscHelper.saveAs(blob, `${slugify(activeList.name)}.csv`);
            });
        },
      },
      {
        group: 'pl.bulk-edit',
        label: 'common.import-csv',
        prefixIcon: <UploadIcon />,
        invisible: activeReadOnly || isInGame || pitches.length === 0,
        onClick: () => {
          if (fileInput.current) {
            fileInput.current.handleClick();
          } else {
            console.warn('no file input element');
          }
        },
      },

      // divider

      {
        group: '_3',
        label: 'common.reset-training-data',
        prefixIcon: <ResetIcon />,
        disabled: !readyToTrain,
        invisible: activeReadOnly || trainedPitches.length === 0,
        color: RADIX.COLOR.WARNING,
        onClick: () =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.Reset,
            pitches: pitches,
          }),
      },
      {
        group: '_3',
        label: 'pl.rename-folder',
        prefixIcon: <InputIcon />,
        invisible: activeReadOnly || isInGame || !activeList?.folder,
        color: RADIX.COLOR.WARNING,
        onClick: () =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.RenameFolder,
          }),
      },
      {
        group: '_3',
        label: 'common.delete',
        prefixIcon: <TrashIcon />,
        invisible: activeReadOnly || isInGame,
        color: RADIX.COLOR.DANGER,
        onClick: () =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.DeleteList,
          }),
      },
    ];

    return actions;
  }, [
    machine.tracking_device,
    activeReadOnly,
    readyToTrain,
    isInGame,
    showRecap,
    pitches,
    activeList,
    userRoleFeatureFlags,
    trainedPitches,
    machine.releaseFt,
    machine.machineID,
    machine.ball_type,
    machine.last_hardware_changed,
    userRoleFeatureFlags.isSuperAdmin,
    activeModel,
  ]);

  const updateListViaCSV = useCallback(
    async (files: File[]) => {
      try {
        if (!activeList) {
          NotifyHelper.error({
            message_md: 'Cannot import file without an active pitch list.',
          });
          return false;
        }

        useListLibraryStore.setState({ loading: true });

        await PitchListsService.getInstance()
          .importCsv(activeList._id, files)
          .finally(() => useListLibraryStore.setState({ loading: false }));

        return true;
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    [activeList]
  );

  const onCSVChange = useCallback(
    async (files: File[]) => {
      const importSuccess = await updateListViaCSV(files);

      if (!importSuccess) {
        NotifyHelper.error({
          message_md: `CSV import was not processed successfully. ${ERROR_MSGS.CONTACT_SUPPORT}`,
        });
        return;
      }

      NotifyHelper.success({
        message_md: 'CSV import processed successfully, refreshing pitches...',
      });

      // reload pitches so the successful changes take effect
      await usePitchListStore.getState().reloadPitches({
        activeList: activeList,
      });
    },
    [activeList]
  );

  return (
    <ErrorBoundary identifier="PitchListHeader">
      <SectionHeader
        loading={loading}
        headerNode={
          activeList ? <PitchListAvatarHeader list={activeList} /> : undefined
        }
        mainAction={{
          label: t('pl.train-n-new-x', {
            n: untrainedPitches.length,
            x: t(
              untrainedPitches.length === 1 ? 'common.pitch' : 'common.pitches'
            ).toLowerCase(),
          }).toString(),
          invisible: untrainedPitches.length === 0,
          disabled: !readyToTrain || !s2mReady,
          variant: 'soft',
          color: RADIX.COLOR.TRAIN_PITCH,
          className: 'text-titlecase',
          onClick: () => props.onTrainFn(untrainedPitches.map((p) => p._id)),
        }}
        actions={actions}
        breadcrumbs={[
          {
            label: 'main.pitches',
            onClick: () => navigate(paths.pitches.library),
          },
          {
            label: 'main.pitch-lists',
            onClick: () => navigate(paths.pitches.lists),
          },
          {
            label: listName,
          },
        ]}
      />

      <CommonSimpleFileUploader
        ref={fileInput}
        id="pitch-list-uploader"
        acceptedTypes={[TextMime.CSV]}
        notifyMode="aggregate"
        onChange={onCSVChange}
        hidden
      />
    </ErrorBoundary>
  );
};
